import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Compte le nombre de messages non lus reçus par l'utilisateur courant
 * (toutes conversations confondues + admin DMs). Se met à jour en temps réel.
 */
export const useUnreadMessages = () => {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!user) {
      setCount(0);
      return;
    }
    const { data: convos } = await supabase
      .from("conversations")
      .select("id")
      .or(`parent_id.eq.${user.id},pro_id.eq.${user.id}`);

    let msgCount = 0;
    if (convos && convos.length > 0) {
      const ids = convos.map((c) => c.id);
      const { count: c1 } = await supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .in("conversation_id", ids)
        .neq("sender_id", user.id)
        .eq("read", false);
      msgCount = c1 ?? 0;
    }

    const { count: c2 } = await supabase
      .from("admin_direct_messages")
      .select("id", { count: "exact", head: true })
      .eq("recipient_id", user.id)
      .eq("read", false);

    setCount(msgCount + (c2 ?? 0));
  }, [user]);

  useEffect(() => {
    if (!user) {
      setCount(0);
      return;
    }
    refresh();

    const channel = supabase
      .channel(`unread-messages-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "messages" }, () => refresh())
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "admin_direct_messages", filter: `recipient_id=eq.${user.id}` },
        () => refresh(),
      )
      .subscribe();

    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [user, refresh]);

  return { unreadMessages: count, refresh };
};
